import { experiences, goals } from 'js/config/convert';
import { union } from 'lodash';
import { makeRequest } from "js/services/Network";
import AppState from 'js/stores/AppState';

const identifierStyle = ['font-weight: bold', 'color: blue'].join(';');
const textStyle = ['font-weight: normal'].join(';');

const runningInProduction = window.app.env === 'production';
const triggeredExperiences = {};
let abExperiments = [];

/**
 * segment.com analytics track function converted to use promises (https://segment.com/docs/sources/website/analytics.js/)
 */
export function track(event, properties, options) {
  return new Promise(resolve => {
    return new Promise(res =>
      analytics.track(event, Object.assign(properties || {}, {}), options, res),
    )
      .catch(() => resolve())
      .then(() => resolve());
  });
}

/**
 * segment.com analytics alias function converted to use promises (https://segment.com/docs/sources/website/analytics.js/)
 */
export function alias(userId, previousId = undefined, options = {}) {
  return new Promise(resolve => {
    return new Promise(res => analytics.alias(userId, previousId, options, res))
      .catch(() => resolve())
      .then(() => resolve());
  });
}

/**
 * segment.com analytics reset function converted to use promises (https://segment.com/docs/sources/website/analytics.js/)
 */
export function reset() {
  try {
    analytics.reset();
  } catch (error) {}
  return Promise.resolve();
}

/**
 * Send page analytics to back-end
 */
export function page(name, title, url) {
  try {
    analytics.page(name, { title, url });
  } catch (error) {
    throw error;
  }
}

export function identify(user) {
  analytics.identify(user.id, {
    name: user.name,
    email: user.email
  });
}

/**
 * Place a command onto the convert.com queue
 * @param {array} command
 */
function convertQueue(command) {
  window._conv_q = window._conv_q || [];
  window._conv_q.push(command);
}

/**
 * Trigger a convert.com goal
 *
 * @param string goalName
 */
export function goal(goalName) {
  const isTestGoal = goals.testing.includes(goalName);
  const shouldTrigger = (runningInProduction && !isTestGoal) || (!runningInProduction && isTestGoal);

  if (shouldTrigger) {
    goals.active[goalName] && convertQueue(goals.active[goalName]);

    if (!runningInProduction) {
      console.log(
        goals.active[goalName]
          ? `Triggered goal %c${goalName}%c was successfully triggered.`
          : `Triggered goal %c${goalName}%c failed to trigger because it is not registered in the configuration.`,
        identifierStyle,
        textStyle,
      );
    }
  } else if (!runningInProduction) {
    console.log(
      `Triggered goal %c${goalName}%c was not actually triggered. This goal is configured for production.`,
      identifierStyle,
      textStyle,
    );
  }
}

/**
 * Run a convert.com experience, and return the variation it defines.
 * This functions result is memoized
 *
 * @param string experienceName
 */
export function experience(experienceName) {
  const isTestExperience = experiences.testing.includes(experienceName);
  let variant = experiences.active[experienceName].defaultVariation || 'original';
  let variantType = 'default';

  const urlParams = new URLSearchParams(window.location.search);
  if (!runningInProduction && urlParams.has(experienceName)) {
    variantType = 'query-string overridden';
  }

  const runData = (experiences.active[experienceName].runData = {
    variant,
    isTestExperience,
    variantType,
    wasTriggered: false,
    variantWasReturned: false,
  });

  if (!(experienceName in triggeredExperiences)) {
    if (!isInitializing) {
      throw 'Cannot instantiate experience outside of page initialization';
    }

    const shouldNotTrigger = (!runningInProduction && !isTestExperience) || (runningInProduction && isTestExperience);

    if (shouldNotTrigger) {
      if (variantType === 'query-string overridden') {
        variant = triggeredExperiences[experienceName] = urlParams.get(experienceName);
      } else {
        variant = triggeredExperiences[experienceName] = variant;
      }
    } else {
      window[experienceName] = true;

      if (variantType === 'query-string overridden') {
        runData.variant = variant = triggeredExperiences[experienceName] = urlParams.get(experienceName);
      } else {
        convertQueue(['run', 'true']);
        runData.wasTriggered = true;
        runData.variant = variant = triggeredExperiences[experienceName] =
          window[`${experienceName}_variation`] || variant;
      }

      if (!runningInProduction) {
        runData.variantWasReturned = typeof window[`${experienceName}_variation`] !== 'undefined';
      }
    }
  } else {
    // experience was already triggered, use previous result from cache
    variant = triggeredExperiences[experienceName];
  }

  return variant;
}

export function loginPrefix() {
  let analyticsPrefix;

  switch (AppState.currentPage) {
    case 'MSellerPage':
      analyticsPrefix = 'SP_mseller_';
      break;
    case 'CreatePaymentLinkPage':
      analyticsPrefix = 'SP_';
      break;
    case 'CheckoutPage':
      analyticsPrefix = 'BP_';
      break;
    default:
      analyticsPrefix = '';
  }

  return analyticsPrefix;
}

function report() {
  abExperiments.length && console.info('abExperiments', abExperiments);
  for (let exp in experiences.active) {
    if (experiences.active[exp].runData) {
      console.log(
        `Triggered experience %c${exp}%c displaying %c${experiences.active[exp].runData.variant}%c`,
        identifierStyle,
        textStyle,
        identifierStyle,
        textStyle,
      );
    }
  }
}

let isInitializing = false;

function initialize(activeExperiences) {
  // trigger all active activeExperiences
  if (activeExperiences) {
    isInitializing = true;
    activeExperiences.forEach(experience);
    isInitializing = false;

    // register all triggered experience variations in the abExperiments tracking list
    activeExperiences.forEach(exp =>
      abExperiments.push(`${experiences.active[exp].trackAs}-${exp.replace(/_/g, '-')}_${triggeredExperiences[exp]}`),
    );
  }

  // load and update experiments from localstorage
  abExperiments = union(JSON.parse(localStorage.getItem('abExperiments')) || [], abExperiments).filter(exp => {
    const trackAs = exp.slice(0, exp.indexOf('-'));
    exp = exp.slice(exp.indexOf('-') + 1);
    exp = exp.slice(0, exp.indexOf('_'));
    exp = exp.replace(/-/g, '_');
    return typeof experiences.active[exp] !== 'undefined' && trackAs === experiences.active[exp].trackAs;
  });
  localStorage.setItem('abExperiments', JSON.stringify(abExperiments));

  // identify the current user
  if (!window.is_404 && window.app.currentUser) {
    analytics.identify(window.app.currentUser.id, {
      name: window.app.currentUser.name,
      email: window.app.currentUser.email,
      ab_experiments: abExperiments,
    });
  }

  !runningInProduction && report();
}

export default {
  track,
  alias,
  reset,
  page,
  goal,
  identify,
  experience,
  initialize,
  loginPrefix,
};
